import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Inbox } from 'lucide-react';
import { api, apiError } from '@/lib/api';
import { money } from '@/lib/format';
import { useI18n } from '@/i18n';
import { useAuth } from '@/context/AuthContext';
import { MethodIcon, Notice, PageLoading, PageTitle } from '@/components/ui';
import { NeedAccount } from './NeedAccount';
import type { Method } from '@/types/api';

/** The method list of one direction (deposit / withdraw), the home country's methods first. */
export function MethodsPage({ direction }: { direction: 'deposit' | 'withdraw' }) {
    const { t, te, lang } = useI18n();
    const { me } = useAuth();
    const navigate = useNavigate();
    const q = useQuery({
        queryKey: ['methods', direction, lang],
        queryFn: async () => (await api.get<{ methods: Method[] }>(`/${direction}/methods`)).data.methods,
        enabled: !!me?.registered,
        staleTime: 30_000
    });
    if (!me?.registered) return <NeedAccount />;
    return (
        <div>
            <PageTitle title={t(`methods.${direction}.title`)} subtitle={t(`methods.${direction}.subtitle`)} />
            {q.isLoading && <PageLoading />}
            {q.isError && <Notice tone="warn">{te(apiError(q.error))}</Notice>}
            {q.data && !q.data.length && (
                <div className="card text-center py-10 space-y-2">
                    <Inbox className="w-8 h-8 mx-auto text-ink-muted" />
                    <p className="text-sm text-ink-muted">{t(`methods.${direction}.none`)}</p>
                </div>
            )}
            <div className="space-y-2.5">
                {q.data?.map(m => (
                    <button key={m.key} className="card card-tap w-full flex items-center gap-3 text-left" onClick={() => navigate(`/${direction}/${m.key}`)}>
                        <MethodIcon kind={m.kind} />
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                                <span className="font-bold truncate">{m.name}</span>
                                {m.bonusPercent > 0 && <span className="badge-lime">+{m.bonusPercent}%</span>}
                            </div>
                            <div className="text-[11px] text-ink-muted mt-0.5">
                                {t('methods.limits', { min: money(m.min, m.currency, lang), max: money(m.max, m.currency, lang) })}
                                {direction === 'withdraw' && m.feePercent > 0 ? ` · ${t('methods.fee', { percent: m.feePercent })}` : ''}
                            </div>
                            <div className="text-[11px] text-ink-muted/80">{t(`kinds.${m.kind}`)}</div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-brand-300" />
                    </button>
                ))}
            </div>
        </div>
    );
}
